export const LINKED_PAYPAL_PAYOUT_REASON =
  "PayPal withdrawals require the provider to sign in and link a verified personal payout account.";

export function createEmptyForm() {
  return {
    providerRole: "freelancer",
    displayName: "",
    headline: "",
    bio: "",
    experienceYears: "",
    portfolioUrl: "",
    cvUrl: "",
    companyName: "",
  };
}

export function createEmptySellerProductForm() {
  return {
    id: 0,
    nameAr: "",
    nameEn: "",
    descriptionAr: "",
    descriptionEn: "",
    categoryId: "",
    price: "",
    salePrice: "",
    color: "",
    image: "",
    imageFile: null,
    imagePreview: "",
    stock: "",
    isDigital: false,
    digitalFilePath: "",
    digitalFile: null,
    status: "active",
  };
}

export function createEmptySellerVariantForm() {
  return {
    id: 0,
    attributeId: "",
    valueId: "",
    colorId: "",
    sizeId: "",
    sku: "",
    price: "",
    salePrice: "",
    stock: "",
    lowStockAlert: "5",
    image: "",
    imageFile: null,
    imagePreview: "",
  };
}

export function createEmptyServiceForm() {
  return {
    id: 0,
    titleAr: "",
    titleEn: "",
    shortDescriptionAr: "",
    shortDescriptionEn: "",
    descriptionAr: "",
    descriptionEn: "",
    price: "",
    currency: "USD",
    deliveryDays: "1",
    revisions: "0",
    coverImage: "",
    isActive: true,
  };
}

export function createEmptyConsultationForm() {
  return {
    id: 0,
    titleAr: "",
    titleEn: "",
    shortDescriptionAr: "",
    shortDescriptionEn: "",
    descriptionAr: "",
    descriptionEn: "",
    price: "",
    durationMinutes: "30",
    coverImage: "",
    meetingProvider: "",
    category: "",
    isActive: true,
  };
}

export function createEmptyCourseLesson() {
  return {
    id: 0,
    titleAr: "",
    titleEn: "",
    slug: "",
    lessonType: "video",
    videoUrl: "",
    contentAr: "",
    contentEn: "",
    attachmentPath: "",
    durationSeconds: "",
    isPreview: false,
  };
}

export function createEmptyCourseSection() {
  return {
    id: 0,
    titleAr: "",
    titleEn: "",
    lessons: [createEmptyCourseLesson()],
  };
}

export function createEmptyCourseForm() {
  return {
    id: 0,
    titleAr: "",
    titleEn: "",
    shortDescriptionAr: "",
    shortDescriptionEn: "",
    descriptionAr: "",
    descriptionEn: "",
    thumbnail: "",
    promoVideoUrl: "",
    price: "",
    salePrice: "",
    isFree: false,
    isActive: true,
    level: "beginner",
    durationText: "",
    category: "",
    requirementsAr: "",
    requirementsEn: "",
    whatYouWillLearnAr: "",
    whatYouWillLearnEn: "",
    sections: [createEmptyCourseSection()],
  };
}

export function createEmptyJobForm() {
  return {
    id: 0,
    titleAr: "",
    titleEn: "",
    shortDescriptionAr: "",
    shortDescriptionEn: "",
    descriptionAr: "",
    descriptionEn: "",
    company: "",
    salaryMin: "",
    salaryMax: "",
    salaryText: "",
    employmentType: "full-time",
    location: "",
    experienceLevel: "",
    coverImage: "",
    applicationDeadline: "",
    isActive: true,
    isFeatured: false,
  };
}

export function createEmptyPayoutMethodForm() {
  return {
    id: 0,
    method: "",
    accountLabel: "",
    accountName: "",
    destinationEmail: "",
    destinationReference: "",
    isDefault: true,
  };
}

export function createEmptyWithdrawalForm() {
  return {
    methodType: "legacy",
    payoutMethodId: "",
    bankAccountId: "",
    amount: "",
    notes: "",
  };
}

export function createEmptyBankAccountForm() {
  return {
    id: 0,
    fullName: "",
    iban: "",
    currency: "",
    bankName: "",
    swiftCode: "",
    addressLine: "",
    city: "",
    postalCode: "",
    isDefault: false,
    status: "active",
  };
}

export function createEmptyPayPalLinkConfig(method = "paypal") {
  return {
    method,
    enabled: false,
    mode: method === "test_paypal" ? "sandbox" : "live",
    env: method === "test_paypal" ? "sandbox" : "production",
    merchantId: "",
    clientIds: { production: "", sandbox: "" },
    reason: "",
  };
}

export function getRoleLabel(role, isAr) {
  const labels = {
    trainer: isAr ? "مدرب" : "Trainer",
    consultant: isAr ? "مستشار" : "Consultant",
    freelancer: isAr ? "مقدم خدمة" : "Freelancer",
    employer: isAr ? "صاحب وظائف" : "Employer",
    seller: isAr ? "بائع" : "Seller",
  };
  return labels[role] || role;
}

export function getStatusLabel(status, isAr) {
  const labels = {
    review: isAr ? "قيد المراجعة" : "Under Review",
    pending: isAr ? "قيد الانتظار" : "Pending",
    approved: isAr ? "تمت الموافقة" : "Approved",
    rejected: isAr ? "مرفوض" : "Rejected",
    active: isAr ? "نشط" : "Active",
    inactive: isAr ? "غير نشط" : "Inactive",
    processing: isAr ? "قيد المعالجة" : "Processing",
    paid: isAr ? "تم الدفع" : "Paid",
    failed: isAr ? "فشل" : "Failed",
  };
  return labels[status] || status;
}

export function getStatusBadgeClass(status) {
  if (status === "approved" || status === "active" || status === "paid") {
    return "bg-success-subtle text-success";
  }
  if (status === "rejected" || status === "inactive" || status === "failed") {
    return "bg-danger-subtle text-danger";
  }
  if (status === "processing") {
    return "bg-info-subtle text-info";
  }
  return "bg-warning-subtle text-warning-emphasis";
}

export function formatDateTime(value) {
  if (!value) return "-";
  try {
    return new Date(String(value).replace(" ", "T")).toLocaleString();
  } catch {
    return value;
  }
}

export function formatMoney(amount, currency = "USD") {
  const value = Number(amount || 0);
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: String(currency || "USD").toUpperCase(),
      maximumFractionDigits: value % 1 === 0 ? 0 : 2,
    }).format(value);
  } catch {
    return `${value.toFixed(2)} ${String(currency || "USD").toUpperCase()}`;
  }
}

export function getPayoutMethodLabel(method, isAr) {
  if (method === "iban_wise") return isAr ? "سحب IBAN" : "IBAN withdrawal";
  if (method === "stripe") return isAr ? "Stripe" : "Stripe";
  if (method === "paypal") return isAr ? "PayPal" : "PayPal";
  if (method === "test_paypal") return isAr ? "PayPal تجريبي" : "PayPal Test";
  return method || (isAr ? "غير معروف" : "Unknown");
}

export function getContentPendingCount(items) {
  return items.filter((item) => item.approvalStatus === "pending").length;
}

export function getDashboardSectionId(key) {
  const map = {
    application: "provider-section-application",
    payouts: "provider-section-payouts",
    seller: "provider-section-seller",
    courses: "provider-section-courses",
    consultations: "provider-section-consultations",
    services: "provider-section-services",
    jobs: "provider-section-jobs",
    products: "provider-section-products",
  };
  return map[key] || "";
}
